import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, PauseCircle, PlayCircle } from "lucide-react";
import api, { errMsg } from "../../lib/api";
import SEO from "../../components/SEO";
import { SelectF, CheckF } from "../admin/AFields";
import { STYLE_THEMES, CARD_SIZES } from "./StudioCard";

export default function StudioSettings() {
  const [studio, setStudio] = useState(null);
  const [auto, setAuto] = useState(null);
  const [groups, setGroups] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/studio/settings").then((r) => setStudio({
      default_language: "bilingual", default_style: "elegant", default_size: "1:1",
      style_rotation: true, require_approval: true, auto_publish: false, auto_broadcast: false, auto_publish_fb: false,
      ...(r.data.studio || {}),
    })).catch((e) => toast.error(errMsg(e)));
    api.get("/wa/automation").then((r) => setAuto(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/greetings/groups").then((r) => setGroups(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/studio/settings", { studio });
      if (auto) await api.put("/wa/automation", auto);
      toast.success("Settings saved — preview approval remains required");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const togglePause = async () => {
    try {
      const r = await api.post(auto.paused ? "/wa/automation/resume" : "/wa/automation/pause-all");
      setAuto({ ...auto, paused: r.data.paused });
      toast.success(r.data.paused ? "ALL AUTOMATION PAUSED" : "Automation resumed");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const toggleGroup = (g) => setAuto({ ...auto, full_auto_groups: (auto.full_auto_groups || []).includes(g) ? auto.full_auto_groups.filter((x) => x !== g) : [...(auto.full_auto_groups || []), g] });

  if (!studio || !auto) return <p className="text-sm text-slate-400">Loading…</p>;

  return (
    <div data-testid="studio-settings">
      <SEO title="Studio Settings" />
      <h1 className="font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl">Automation & Settings</h1>
      <p className="mt-1 text-sm text-slate-500">Safe Mode is enforced during consolidation: AI prepares, the Superadmin previews and approves.</p>

      <div className="mt-8 max-w-3xl space-y-6">
        <div className={`rounded-lg border p-6 ${auto.paused ? "border-red-200 bg-red-50/50" : "border-gray-200 bg-white"}`} data-testid="automation-panel">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="font-display text-lg font-bold text-navy">Automation</h2>
              <p className="mt-1 text-xs text-slate-500">Preview approval is required before public publishing, WhatsApp delivery or Facebook posting.</p>
            </div>
            <button onClick={togglePause} data-testid="settings-pause-all"
              className={`flex items-center gap-2 rounded-md px-5 py-2.5 text-sm font-bold ${auto.paused ? "bg-brandgreen-100 text-brandgreen" : "bg-red-600 text-white hover:bg-red-700"}`}>
              {auto.paused ? <PlayCircle size={16} /> : <PauseCircle size={16} />} {auto.paused ? "Resume Automation" : "PAUSE ALL AUTOMATION"}
            </button>
          </div>
          <div className="mt-5 space-y-4">
            <CheckF label="AUTO GENERATE — prepare AI greeting drafts for upcoming occasions (safe, recommended)" value={auto.auto_generate} onChange={(v) => setAuto({ ...auto, auto_generate: v })} />
            <CheckF label="AUTO IMAGE — also generate an AI image for each auto-prepared greeting (uses AI credits)" value={auto.auto_image} onChange={(v) => setAuto({ ...auto, auto_image: v })} />
            <CheckF label="AUTO WHATSAPP SEND — currently OFF for consolidation testing" value={auto.auto_send} onChange={(v) => setAuto({ ...auto, auto_send: v })} />
            <CheckF label="FULL AUTO SEND — schedule campaigns automatically for the groups below (enable only after WhatsApp test message succeeds)" value={auto.full_auto} onChange={(v) => setAuto({ ...auto, full_auto: v })} />
            <label className="flex items-center gap-2.5 rounded-md border border-brandgreen/25 bg-brandgreen-50 px-4 py-3 text-sm font-semibold text-brandgreen" data-testid="preview-required-setting">
              <input type="checkbox" checked readOnly className="h-4 w-4 accent-brandgreen" />
              PREVIEW REQUIRED — ON (protected)
            </label>
          </div>
          {auto.full_auto && (
            <div className="mt-4 rounded-md bg-navy-50 p-4" data-testid="full-auto-groups">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Full Auto applies to groups</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {groups.map((g) => (
                  <button key={g.id} type="button" onClick={() => toggleGroup(g.name)} data-testid={`auto-group-${g.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className={`rounded-full px-4 py-2 text-xs font-bold ${auto.full_auto_groups?.includes(g.name) ? "bg-navy text-white" : "border border-gray-200 bg-white text-navy"}`}>{g.name}</button>
                ))}
                {groups.length === 0 && <p className="text-xs text-slate-400">Create contact groups first.</p>}
              </div>
            </div>
          )}
          <div className="mt-5 grid gap-5 sm:grid-cols-2">
            <SelectF label="Prepare drafts this many days before the occasion" value={String(auto.occasion_lead_days)} onChange={(v) => setAuto({ ...auto, occasion_lead_days: Number(v) })} options={["0", "1", "2", "3"]} />
            <SelectF label="Default greeting language" value={auto.default_language} onChange={(v) => setAuto({ ...auto, default_language: v })} options={["english", "hindi", "bilingual"]} />
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <h2 className="font-display text-lg font-bold text-navy">Greeting Defaults</h2>
          <div className="mt-4 grid gap-5 sm:grid-cols-3">
            <SelectF label="Default Language" value={studio.default_language} onChange={(v) => setStudio({ ...studio, default_language: v })} options={["english", "hindi", "bilingual"]} />
            <SelectF label="Default Card Style" value={studio.default_style} onChange={(v) => setStudio({ ...studio, default_style: v })} options={Object.keys(STYLE_THEMES)} />
            <SelectF label="Default Card Size" value={studio.default_size} onChange={(v) => setStudio({ ...studio, default_size: v })} options={Object.keys(CARD_SIZES)} />
          </div>
          <div className="mt-6 space-y-4">
            <CheckF label="Daily style auto-rotation (never repeat a style within 7 days)" value={studio.style_rotation} onChange={(v) => setStudio({ ...studio, style_rotation: v })} />
            <CheckF label="Require manual approval before any greeting is published" value={studio.require_approval} onChange={(v) => setStudio({ ...studio, require_approval: v })} />
            <CheckF label="Auto Publish — OFF during consolidation" value={studio.auto_publish} onChange={(v) => setStudio({ ...studio, auto_publish: v })} />
            <CheckF label="Auto WhatsApp broadcast — OFF during consolidation" value={studio.auto_broadcast} onChange={(v) => setStudio({ ...studio, auto_broadcast: v })} />
            <CheckF label="Auto Facebook publish — OFF during consolidation" value={studio.auto_publish_fb} onChange={(v) => setStudio({ ...studio, auto_publish_fb: v })} />
          </div>
        </div>

        <div className="flex justify-end">
          <button onClick={save} disabled={busy} className="btn-primary !rounded-md !px-6 !py-2.5 text-sm disabled:opacity-50" data-testid="studio-settings-save">
            {busy && <Loader2 size={14} className="animate-spin" />} Save Settings
          </button>
        </div>
      </div>
    </div>
  );
}
